import React, { useEffect, useState } from "react";
import api from "../lib/api";
import GemBadge, { TIERS } from "./GemBadge";
import { TrendingUp, ChevronRight, Info, X, Trophy } from "lucide-react";

function StatPill({ k, v }) {
  return (
    <div className="rounded-xl bg-white/[0.04] border border-white/10 px-3 py-2 flex items-center justify-between">
      <span className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">{k}</span>
      <span className="text-sm font-black">{v}</span>
    </div>
  );
}

/**
 * ReputationCardCompact — the redesigned reputation card that SUPPORTS
 * the profile rather than dominates it. Tap to open a full detail sheet.
 *
 * Props:
 *   userId       Optional — if provided, uses public /users/{id}/reputation.
 *                Otherwise pulls /reputation/me (owner).
 *   onClickOpen  Optional override — otherwise opens internal sheet.
 */
export default function ReputationCardCompact({ userId, onClickOpen }) {
  const [rep, setRep] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [achievements, setAchievements] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const url = userId ? `/users/${userId}/reputation` : "/reputation/me";
        const { data } = await api.get(url);
        setRep(data);
      } catch { /* ignore */ }
    })();
  }, [userId]);

  useEffect(() => {
    if (!detailOpen || !rep) return;
    // Owner achievements come from /users/{id}/achievements; owner uses their own id
    const target = userId || rep.user_id || rep.target_id;
    if (!target) return;
    (async () => {
      try {
        const { data } = await api.get(`/users/${target}/achievements`);
        setAchievements(data.items || []);
      } catch { /* ignore */ }
    })();
  }, [detailOpen, rep, userId]);

  if (!rep) return null;
  const t = rep.tier;
  const next = rep.next_tier;
  const tierMeta = TIERS[t.key] || TIERS.white;
  const nextMeta = next ? (TIERS[next.key] || null) : null;
  const pct = Math.round((rep.progress || 0) * 100);

  const openDetail = () => {
    if (onClickOpen) onClickOpen();
    else setDetailOpen(true);
  };

  return (
    <>
      <button
        type="button"
        onClick={openDetail}
        className="w-full text-left rounded-2xl p-4 border relative overflow-hidden transition-all hover:brightness-110 group"
        style={{
          background: `linear-gradient(135deg, ${tierMeta.color}1a 0%, transparent 60%)`,
          borderColor: `${tierMeta.color}44`,
          boxShadow: `0 0 25px -12px ${tierMeta.color}66`,
        }}
        data-testid="reputation-card-compact"
      >
        <div className="flex items-center gap-3">
          <GemBadge tier={t.key} size="lg" />
          <div className="min-w-0 flex-1">
            <p className="text-[10px] uppercase tracking-widest font-black" style={{ color: tierMeta.color }}>
              {t.label}
            </p>
            <p className="text-lg font-black tracking-tighter leading-tight" data-testid="reputation-tier-name">{t.name}</p>
            <div className="mt-1 flex items-center gap-2">
              <span className="text-xs text-zinc-300 font-bold" data-testid="reputation-score">{rep.score}</span>
              <span className="text-[10px] text-zinc-500 uppercase tracking-widest">pts</span>
              {nextMeta ? (
                <span className="text-[10px] text-zinc-500 inline-flex items-center gap-0.5 ml-auto">
                  {rep.points_to_next} to <span style={{ color: nextMeta.color }} className="font-bold">{next.name}</span>
                </span>
              ) : (
                <span className="text-[10px] text-zinc-400 inline-flex items-center gap-1 ml-auto"><TrendingUp className="w-3 h-3" /> Max tier</span>
              )}
            </div>
            <div className="mt-1.5 h-1.5 rounded-full bg-white/5 overflow-hidden" data-testid="reputation-progress-bar">
              <div
                className="h-full transition-all"
                style={{
                  width: `${pct}%`,
                  background: nextMeta ? `linear-gradient(90deg, ${tierMeta.color}, ${nextMeta.color})` : tierMeta.color,
                }}
              />
            </div>
          </div>
          <ChevronRight className="w-4 h-4 text-zinc-500 group-hover:text-zinc-200 transition-colors" />
        </div>
      </button>

      {/* Detail sheet */}
      {detailOpen && (
        <div
          className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm grid place-items-center p-4 sm:p-6"
          onClick={() => setDetailOpen(false)}
          data-testid="reputation-detail-overlay"
        >
          <div
            className="w-full max-w-lg max-h-[85vh] overflow-y-auto rounded-3xl border border-white/10 p-5 relative"
            style={{
              background: `linear-gradient(135deg, ${tierMeta.color}12 0%, #09090b 60%)`,
              boxShadow: `0 0 60px -12px ${tierMeta.color}55`,
            }}
            onClick={(e) => e.stopPropagation()}
            data-testid="reputation-detail-sheet"
          >
            <button
              onClick={() => setDetailOpen(false)}
              className="absolute top-3 right-3 h-8 w-8 grid place-items-center rounded-full bg-white/5 hover:bg-white/10 border border-white/10"
              aria-label="Close"
              data-testid="reputation-detail-close"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="flex items-center gap-3">
              <GemBadge tier={t.key} size="xl" />
              <div>
                <p className="text-[10px] uppercase tracking-widest font-bold" style={{ color: tierMeta.color }}>
                  {t.label}
                </p>
                <p className="text-2xl font-black tracking-tighter">{t.name}</p>
                <p className="text-xs text-zinc-400">
                  <span className="font-bold text-zinc-200">{rep.score}</span> reputation points
                </p>
              </div>
            </div>

            {/* Progress */}
            <div className="mt-4 space-y-2">
              <div className="flex items-center justify-between text-[11px] uppercase tracking-widest font-bold">
                <span style={{ color: tierMeta.color }}>{t.name}</span>
                {nextMeta ? (
                  <span className="text-zinc-400 inline-flex items-center gap-1">
                    {rep.points_to_next} pts to <span style={{ color: nextMeta.color }}>{next.name}</span>
                  </span>
                ) : (
                  <span className="text-zinc-400 inline-flex items-center gap-1"><TrendingUp className="w-3 h-3" /> Max tier</span>
                )}
              </div>
              <div className="h-2 rounded-full bg-white/5 overflow-hidden">
                <div
                  className="h-full transition-all"
                  style={{
                    width: `${pct}%`,
                    background: nextMeta ? `linear-gradient(90deg, ${tierMeta.color}, ${nextMeta.color})` : tierMeta.color,
                  }}
                />
              </div>
            </div>

            {/* Score breakdown */}
            {rep.breakdown && (
              <>
                <h4 className="mt-5 text-[10px] uppercase tracking-widest text-zinc-400 font-black flex items-center gap-1.5">
                  <Info className="w-3.5 h-3.5" /> Score breakdown
                </h4>
                <div className="mt-2 grid grid-cols-2 gap-2" data-testid="reputation-breakdown">
                  <StatPill k="Account age" v={`${rep.breakdown.account_age_pts} pts`} />
                  <StatPill k="Active days" v={`${rep.breakdown.daily_pts} pts`} />
                  {rep.breakdown.completed_orders_pts > 0 && <StatPill k="Orders made" v={`${rep.breakdown.completed_orders_pts} pts`} />}
                  {rep.breakdown.completed_sales_pts > 0 && <StatPill k="Sales" v={`${rep.breakdown.completed_sales_pts} pts`} />}
                  {rep.breakdown.positive_reviews_pts > 0 && <StatPill k="5★ reviews" v={`${rep.breakdown.positive_reviews_pts} pts`} />}
                  {rep.breakdown.repeat_customers_pts > 0 && <StatPill k="Repeat buyers" v={`${rep.breakdown.repeat_customers_pts} pts`} />}
                  {rep.breakdown.tips_received_pts > 0 && <StatPill k="Tips" v={`${rep.breakdown.tips_received_pts} pts`} />}
                  {rep.breakdown.followers_pts > 0 && <StatPill k="Followers" v={`${rep.breakdown.followers_pts} pts`} />}
                  {rep.breakdown.post_pts > 0 && <StatPill k="Posts" v={`${rep.breakdown.post_pts} pts`} />}
                  {rep.breakdown.comments_pts > 0 && <StatPill k="Comments" v={`${rep.breakdown.comments_pts} pts`} />}
                  {rep.breakdown.likes_received_pts > 0 && <StatPill k="Likes received" v={`${rep.breakdown.likes_received_pts} pts`} />}
                  {rep.breakdown.verified_pts > 0 && <StatPill k="Verified bonus" v={`+${rep.breakdown.verified_pts} pts`} />}
                  {rep.breakdown.response_pts > 0 && <StatPill k="Quick replies" v={`+${rep.breakdown.response_pts} pts`} />}
                  {rep.breakdown.consistency_pts > 0 && <StatPill k="Consistency" v={`+${rep.breakdown.consistency_pts} pts`} />}
                </div>
              </>
            )}

            {/* Achievements */}
            {achievements.length > 0 && (
              <>
                <h4 className="mt-5 text-[10px] uppercase tracking-widest text-zinc-400 font-black flex items-center gap-1.5">
                  <Trophy className="w-3.5 h-3.5" /> Achievements · {achievements.length}
                </h4>
                <div className="mt-2 flex flex-wrap gap-1.5" data-testid="reputation-detail-achievements">
                  {achievements.map((a) => (
                    <span
                      key={a.id}
                      className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-1 rounded-full font-black border"
                      style={{ color: a.color || "#39FF14", borderColor: `${a.color || "#39FF14"}55`, background: `${a.color || "#39FF14"}14` }}
                      title={a.description}
                    >
                      {a.label}
                    </span>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
